import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { UrlCopy } from '@/screens/shared/UrlCopy'
import { Badge, Can, Drawer, EmptyState, JsonView, PromptDialog, formatRelative, notify, type BadgeTone } from '@/kit/ui'
import { apiFileFor, endpointLabel, endpointOf, outcomesOf } from '@/lib/endpoints'
import { KNOWN_OUTCOMES } from '@/lib/outputCheck'
import { useCurrentUser } from '@/shell/useCurrentUser'
import { useCodeStore } from '@/store/codeStore'
import type { ApiAnswer, ApiStatus, ApiWhitelistState, CapturedCode, Lender, LenderApi } from '@/types'
import { ApiFieldsDrawer } from './ApiFieldsDrawer'
import { CreateApiDrawer } from './CreateApiDrawer'
import { playgroundUrl } from '../links'
import { ofCode } from '@/lib/batch'
import { ProductPicker, useBatchProduct } from './ProductPicker'

const STATUS: Record<ApiStatus, { label: string; tone: BadgeTone }> = { active: { label: 'active', tone: 'success' }, old_kept: { label: 'old · kept', tone: 'gray' }, old_unused: { label: 'old · unused', tone: 'gray' } }
const WL: Record<ApiWhitelistState, { label: string; tone: BadgeTone }> = { done: { label: 'done', tone: 'success' }, requested: { label: 'requested', tone: 'warning' }, lender_removed: { label: 'lender removed', tone: 'warning' }, not_needed: { label: 'not needed', tone: 'gray' } }
const ANSWER: Record<ApiAnswer, string> = { none: '', one: 'one offer', list: 'offer list' }
const KNOWN: Record<CapturedCode['known'], { label: string; tone: BadgeTone }> = { known: { label: 'known', tone: 'success' }, new: { label: 'new · alert open', tone: 'danger' }, new_shape: { label: 'new shape', tone: 'warning' } }

/**
 * 3.1 Lender APIs (F2-2, F2-3, F2-6): one record per endpoint of the batch, Create API, captured codes, the token and
 * whitelist rules. BP-1: the endpoints of the chosen product's batch (the product picker); Create API adds to it.
 */
export function ApisSubTab({ lender }: { lender: Lender }) {
  const navigate = useNavigate()
  const { user } = useCurrentUser()
  const allApis = useCodeStore((s) => s.apis)
  const batches = useCodeStore((s) => s.batches)
  const allCaptured = useCodeStore((s) => s.captured)
  const addCapturedAsTest = useCodeStore((s) => s.addCapturedAsTest)
  const [creating, setCreating] = useState(false)
  const [adding, setAdding] = useState<CapturedCode | null>(null)
  const [viewing, setViewing] = useState<CapturedCode | null>(null)
  const [fieldsOf, setFieldsOf] = useState<LenderApi | null>(null)

  const apis = useMemo(() => allApis.filter((a) => a.lenderSlug === lender.slug), [allApis, lender.slug])
  const captured = useMemo(() => allCaptured.filter((c) => c.lenderSlug === lender.slug), [allCaptured, lender.slug])
  const { choices, chosen, productSlug, choose } = useBatchProduct(lender)
  const mine = useMemo(() => ofCode(batches, lender.slug, productSlug), [batches, lender.slug, productSlug])
  const shown = mine.find((b) => b.status === 'draft' || b.status === 'tests_passed') ?? mine.find((b) => b.status === 'built') ?? mine.find((b) => b.status === 'live')
  const live = mine.find((b) => b.status === 'live')
  const open = (route: string) => navigate(playgroundUrl(lender.slug, shown?.version, endpointOf(shown?.manifest, route)?.file ?? apiFileFor(shown?.files ?? [], route), productSlug))
  // endpoints the code defines that have no record yet (written in the playground, found by discovery)
  const unrecorded = (shown?.manifest.endpoints ?? []).filter((e) => !apis.some((a) => a.endpoint === e.path))

  return (
    <>
      <ProductPicker choices={choices} chosen={chosen} onChoose={choose} />
      <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
        <p className="max-w-3xl text-xs text-muted">
          Every API the lender gives us, old ones too. Each is one <b>REST endpoint of the batch</b> (a file under <code>src/api/</code>, folder = route) plus this record. Create makes the endpoint file and its test event; Update opens the playground on it. Endpoints written in the playground appear here after Discover or Save. Their addresses (UAT / production, curl, Postman): <Link className="text-primary underline" to={`/catalogue?lender=${lender.slug}`}>API catalogue</Link>.
        </p>
        <Can permission="code.edit">
          <button type="button" className="btn btn-primary" disabled={!chosen} onClick={() => setCreating(true)} data-testid="create-api-open">+ Create API</button>
        </Can>
      </div>
      {apis.length === 0 && unrecorded.length === 0 ? (
        <EmptyState title="No API yet" description="Create API makes the first endpoint file and its test event in a draft batch." />
      ) : (
        <div className="card-flush overflow-x-auto">
          <table className="table">
            <thead>
              <tr><th>API</th><th>Our endpoint{shown ? ` (${shown.version} ${shown.status.replace('_', ' ')})` : ''}</th><th>Outcomes</th><th>Lender's method · path</th><th>Auth</th><th>IP whitelist</th><th>Status</th><th title="What it answers, and the extra fields it needs (J2)">Needs</th><th>Used in</th><th>Last real call</th><th /></tr>
            </thead>
            <tbody>
              {apis.map((a) => {
                const ep = endpointOf(shown?.manifest, a.endpoint)
                return (
                  <tr key={a.id} data-testid={`api-row-${endpointLabel(a.endpoint)}`}>
                    <td><b>{a.name}</b>{a.auth === 'token_source' && <Badge tone="accent" className="ml-1">token source</Badge>}</td>
                    <td className="mono">{ep?.method ?? a.method} {a.endpoint}{shown && !ep && <Badge tone="warning" className="ml-1" title="The record exists but no file under src/api/ gives this route in this batch">missing</Badge>}{ep && <UrlCopy slug={lender.slug} route={a.endpoint} live={!!live} />}</td>
                    <td className="mono text-2xs">{shown && ep ? outcomesOf(shown.manifest, shown.tests, a.endpoint).join(' · ') : '—'}</td>
                    <td className="mono">{a.method} {a.path}</td>
                    <td>{a.authNote}</td>
                    <td><Badge tone={WL[a.whitelist.state].tone}>{WL[a.whitelist.state].label}{a.whitelist.date ? ` ${a.whitelist.date}` : ''}</Badge></td>
                    <td><Badge tone={STATUS[a.status].tone}>{STATUS[a.status].label}</Badge></td>
                    <td className="whitespace-nowrap" data-testid={`api-needs-${endpointLabel(a.endpoint)}`}>
                      {a.answer !== 'none' && <Badge tone="accent" className="mr-1">{ANSWER[a.answer]}</Badge>}
                      <span className="text-2xs text-muted">{a.fields.length ? `${a.fields.length} field${a.fields.length === 1 ? '' : 's'}` : 'no extra fields'}</span>
                      <Can permission="code.edit">
                        <button type="button" className="btn btn-ghost btn-sm ml-1" onClick={() => setFieldsOf(a)} data-testid={`api-fields-${endpointLabel(a.endpoint)}`}>Fields</button>
                      </Can>
                    </td>
                    <td>{a.usedIn}</td>
                    <td className="mono whitespace-nowrap">{a.lastCall ? `${formatRelative(a.lastCall.at)} · ${a.lastCall.httpStatus}` : '—'}</td>
                    <td className="text-right">
                      <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(a.endpoint)} disabled={!shown}>{a.status === 'active' ? 'Update' : 'View'}</button>
                    </td>
                  </tr>
                )
              })}
              {unrecorded.map((e) => (
                <tr key={e.path} className="bg-amber-50/60 dark:bg-amber-950/20" data-testid={`api-row-${endpointLabel(e.path)}`}>
                  <td><i>{e.title ?? endpointLabel(e.path)}</i> <Badge tone="warning" className="ml-1">no record yet</Badge></td>
                  <td className="mono">{e.method} {e.path}</td>
                  <td className="mono text-2xs">{e.outcomes.join(' · ')}</td>
                  <td colSpan={6} className="text-2xs text-muted">Found in the code (file {e.file}); the lender's method, path, auth and whitelist are not recorded yet.</td>
                  <td className="text-right"><button type="button" className="btn btn-ghost btn-sm" onClick={() => open(e.path)}>Open</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-3 grid grid-cols-1 gap-3 lg:grid-cols-2">
        <section className="card">
          <h2 className="section-title">Captured from this lender (G11)</h2>
          {captured.length === 0 ? (
            <p className="text-xs text-muted">No reply code captured yet. Every code and reply shape the lender sends in testing or production lands here, counted; a new one raises an alert.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="table">
                <thead><tr><th>Code / reply shape</th><th>Endpoint</th><th className="text-right">UAT</th><th className="text-right">Prod</th><th>Last seen</th><th>Known?</th><th>Reply</th></tr></thead>
                <tbody>
                  {captured.map((c) => (
                    <tr key={c.id}>
                      <td className="mono">{c.code}</td>
                      <td className="mono">{endpointLabel(c.endpoint)}</td>
                      <td className="text-right">{c.uat}</td>
                      <td className="text-right">{c.prod}</td>
                      <td className="mono whitespace-nowrap">{formatRelative(c.lastSeen)}</td>
                      <td className="whitespace-nowrap">
                        <Badge tone={KNOWN[c.known].tone}>{c.known === 'known' ? `known → ${c.outcome}` : KNOWN[c.known].label}</Badge>
                        {c.known !== 'known' && (
                          <Can permission="code.edit">
                            <button type="button" className="btn btn-ghost btn-sm ml-1" onClick={() => setAdding(c)} data-testid={`add-test-${c.id}`}>Add as a test</button>
                          </Can>
                        )}
                      </td>
                      <td><button type="button" className="btn btn-ghost btn-sm" onClick={() => setViewing(c)} data-testid={`view-reply-${c.id}`}>View reply</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
        <section className="card">
          <h2 className="section-title">Token &amp; whitelist rules (F2-6)</h2>
          <p className="text-xs text-muted">
            <b>Token:</b> the token-source endpoint is called by the Gateway itself; every API with auth = bearer takes the cached token (refreshed 5 min before expiry) as <code>ctx.env.LENDER_TOKEN</code>. A workflow never calls the token endpoint. Code never holds a key: <code>ctx.env.NAME</code> / <code>ctx.secret('NAME')</code> come from the Env variables tab (tab 5); the live container gets them from the vault.
          </p>
          <p className="mt-2 text-xs text-muted">
            <b>IP whitelist:</b> our outgoing IPs are fixed (<code>UAT 13.233.xx.xx · Prod 3.109.xx.xx</code>, Lender info); the lender confirms, the date is recorded per API; a 403 from a whitelisted IP raises an alert.
          </p>
          <p className="mt-2 text-2xs text-muted">Variables of {lender.name}: {lender.envVars.length ? lender.envVars.map((v) => v.name).join(', ') : 'none yet'} · whitelist {lender.ipWhitelist.state.replace('_', ' ')}{lender.ipWhitelist.date ? ` ${lender.ipWhitelist.date}` : ''}.</p>
        </section>
      </div>
      <CreateApiDrawer lender={lender} productSlug={productSlug} productLabel={chosen?.label ?? ''} isOpen={creating} onClose={() => setCreating(false)} />
      <ApiFieldsDrawer api={fieldsOf} onClose={() => setFieldsOf(null)} />
      <Drawer isOpen={viewing !== null} onClose={() => setViewing(null)} size="lg" title={`Reply ${viewing?.code ?? ''}`} subtitle={viewing ? `${endpointLabel(viewing.endpoint)} · HTTP ${viewing.sampleReply.status} · first seen as this code, masked` : ''}>
        {viewing && (
          <div className="space-y-2 text-xs" data-testid="captured-reply">
            <p className="text-muted">The lender's reply the first time this code came (numbers and personal details masked: a shape, not a person). A run's own full reply: Workflow runs → the run → Show lender reply (admins).</p>
            <JsonView title={`HTTP ${viewing.sampleReply.status}`} value={viewing.sampleReply.body ?? null} maxHeight={560} />
          </div>
        )}
      </Drawer>
      <PromptDialog
        isOpen={adding !== null}
        onClose={() => setAdding(null)}
        title={`Add “${adding?.code ?? ''}” as a test event`}
        message={`Reply ${adding?.sampleReply.status ?? ''} on ${adding ? endpointLabel(adding.endpoint) : ''}, as captured. Which outcome must the handler give it? Outcomes of this endpoint so far: ${adding && shown ? outcomesOf(shown.manifest, shown.tests, adding.endpoint).join(', ') : ''}.`}
        inputLabel="Expected outcome"
        initialValue={adding?.known === 'new_shape' ? 'offer' : 'error'}
        validate={(v) => ((KNOWN_OUTCOMES as readonly string[]).includes(v) ? null : `One the system knows: ${KNOWN_OUTCOMES.join(', ')}`)}
        confirmText="Add as a test"
        onConfirm={(outcome) => {
          if (!adding) return
          const batchId = addCapturedAsTest(adding.id, outcome, user.email, productSlug)
          const b = useCodeStore.getState().batches.find((x) => x.id === batchId)!
          notify.success('Test event added', `“${adding.code} (captured)” → ${outcome} in batch ${b.version}; the alert is acknowledged.`)
          setAdding(null)
          navigate(playgroundUrl(lender.slug, b.version, endpointOf(b.manifest, adding.endpoint)?.file, b.productSlug ?? null))
        }}
      />
    </>
  )
}
